import React, { useState, useEffect } from 'react';
import { Link, useNavigate, useSearchParams } from 'react-router-dom';
import { motion } from 'framer-motion';
import { 
  ArrowLeft, 
  Crown, 
  Check, 
  MessageSquare, 
  Phone, 
  Shield, 
  Zap,
  Users,
  Loader2,
  CreditCard,
  Smartphone,
  Building2,
  CheckCircle,
  XCircle
} from 'lucide-react';
import { toast } from 'sonner';
import { useAuth } from '../contexts/AuthContext';
import api from '../services/api';

// Panchi Bird Logo
const PanchiBirdLogo = ({ className = "w-10 h-10" }) => (
  <img 
    src="https://customer-assets.emergentagent.com/job_panchi-travel-1/artifacts/5hhgrsb0_20260119_170240_0000.png"
    alt="Panchi"
    className={className}
    style={{ objectFit: 'contain' }}
  />
);

// Load Razorpay script
const loadRazorpayScript = () => {
  return new Promise((resolve) => {
    if (window.Razorpay) {
      resolve(true);
      return;
    }
    const script = document.createElement('script');
    script.src = 'https://checkout.razorpay.com/v1/checkout.js';
    script.onload = () => resolve(true);
    script.onerror = () => resolve(false);
    document.body.appendChild(script);
  });
};

export default function Payment() {
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();
  const { user, isAuthenticated } = useAuth();
  
  // Get plan from URL or default to monthly
  const urlPlan = searchParams.get('plan') || 'monthly';
  const orderId = searchParams.get('order_id');
  const paymentStatus = searchParams.get('status');
  
  const [selectedPlan, setSelectedPlan] = useState(urlPlan);
  const [loading, setLoading] = useState(false);
  const [pricing, setPricing] = useState(null);
  const [showResult, setShowResult] = useState(!!orderId);
  const [paymentResult, setPaymentResult] = useState(null);
  
  // Pro features
  const PRO_FEATURES = [
    { icon: MessageSquare, text: 'Trip-specific chat with matched travelers' },
    { icon: Phone, text: 'Optional contact exchange (with consent)' },
    { icon: Zap, text: 'Priority in travel buddy matching' },
    { icon: Users, text: 'Unlimited trip posts and join requests' },
    { icon: Shield, text: 'AI-moderated safe communication' },
  ];
  
  useEffect(() => {
    loadPricing();
    loadRazorpayScript();
    
    // If we have order_id in URL, this is a payment result
    if (orderId && paymentStatus) {
      checkPaymentStatus(orderId);
    }
  }, [orderId, paymentStatus]);
  
  const loadPricing = async () => {
    try {
      const res = await api.getProPricing();
      if (res?.pricing) {
        setPricing(res.pricing);
      }
    } catch (error) {
      console.error('Load pricing error:', error);
    }
  };
  
  const checkPaymentStatus = async (orderId) => {
    try {
      const res = await api.getPaymentStatus(orderId);
      if (res?.payment) {
        setPaymentResult(res.payment);
        setShowResult(true);
      }
    } catch (error) {
      console.error('Check payment status error:', error);
      setPaymentResult({ status: 'failed', error: 'Could not verify payment' });
      setShowResult(true);
    }
  };
  
  const handlePayment = async () => {
    if (!isAuthenticated) {
      toast.error('Please sign in to upgrade to Pro');
      navigate('/auth');
      return;
    }
    
    // Check if Razorpay is loaded
    const isLoaded = await loadRazorpayScript();
    if (!isLoaded) {
      toast.error('Payment system failed to load. Please refresh and try again.');
      return;
    }
    
    setLoading(true);
    
    try {
      // Create order
      const orderRes = await api.createRazorpayOrder({
        plan: selectedPlan,
        user_id: user?.user_id || 'default_user',
        email: user?.email,
        phone: user?.phone,
        name: user?.name
      });
      
      if (!orderRes?.success) {
        toast.error(orderRes?.error || 'Failed to create order');
        setLoading(false);
        return;
      }
      
      // Configure Razorpay options
      const options = {
        key: orderRes.key_id,
        amount: orderRes.amount,
        currency: orderRes.currency,
        name: 'Panchi',
        description: orderRes.description,
        order_id: orderRes.order_id,
        image: 'https://customer-assets.emergentagent.com/job_panchi-travel-1/artifacts/5hhgrsb0_20260119_170240_0000.png',
        handler: async function (response) {
          // Payment successful - verify on backend
          try {
            const verifyRes = await api.verifyRazorpayPayment({
              razorpay_order_id: response.razorpay_order_id,
              razorpay_payment_id: response.razorpay_payment_id,
              razorpay_signature: response.razorpay_signature,
              user_id: user?.user_id || 'default_user',
              plan: selectedPlan
            });
            
            if (verifyRes?.success) {
              setPaymentResult({ status: 'success', order_id: response.razorpay_order_id });
              setShowResult(true);
              toast.success('Payment successful! Welcome to Pro!');
            } else {
              setPaymentResult({ status: 'failed', error: verifyRes?.error });
              setShowResult(true);
              toast.error('Payment verification failed');
            }
          } catch (error) {
            console.error('Verification error:', error);
            setPaymentResult({ status: 'failed', error: 'Verification failed' });
            setShowResult(true);
          }
          setLoading(false);
        },
        prefill: orderRes.prefill,
        notes: {
          plan: selectedPlan,
          product: 'Panchi Pro Membership'
        },
        theme: {
          color: '#14B8A6', // Teal color to match Panchi theme
          backdrop_color: 'rgba(0, 0, 0, 0.5)'
        },
        modal: {
          ondismiss: function() {
            setLoading(false);
            toast.info('Payment cancelled');
          }
        }
      };
      
      // Open Razorpay checkout
      const razorpay = new window.Razorpay(options);
      razorpay.on('payment.failed', function (response) {
        setPaymentResult({ 
          status: 'failed', 
          error: response.error.description,
          order_id: orderRes.order_id 
        });
        setShowResult(true);
        setLoading(false);
        toast.error('Payment failed: ' + response.error.description);
      });
      razorpay.open();
      
    } catch (error) {
      console.error('Payment error:', error);
      toast.error('Failed to initiate payment');
      setLoading(false);
    }
  };
  
  // Payment Result View
  if (showResult) {
    const isSuccess = paymentResult?.status === 'success';
    
    return (
      <div className="min-h-screen bg-gradient-to-br from-sky-50 via-white to-teal-50 flex items-center justify-center p-4">
        <motion.div
          initial={{ scale: 0.9, opacity: 0 }}
          animate={{ scale: 1, opacity: 1 }}
          className="w-full max-w-md bg-white shadow-2xl overflow-hidden"
          style={{ borderRadius: '1.5rem' }}
        >
          <div className={`p-8 text-center ${
            isSuccess 
              ? 'bg-gradient-to-br from-emerald-400 to-teal-500' 
              : 'bg-gradient-to-br from-red-400 to-rose-500'
          } text-white`}>
            {isSuccess ? (
              <CheckCircle size={64} className="mx-auto mb-4" />
            ) : (
              <XCircle size={64} className="mx-auto mb-4" />
            )}
            <h2 className="text-2xl font-black mb-2">
              {isSuccess ? 'Payment Successful!' : 'Payment Failed'}
            </h2>
            <p className="text-white/90">
              {isSuccess 
                ? 'Welcome to Panchi Pro! Your membership is now active.' 
                : paymentResult?.error || 'Your payment could not be processed.'}
            </p>
          </div>
          
          <div className="p-6">
            {isSuccess && (
              <div className="space-y-3 mb-6">
                <div className="flex items-center gap-3 p-3 bg-emerald-50" style={{ borderRadius: '0.75rem' }}>
                  <Check className="text-emerald-600" size={20} />
                  <span className="text-gray-700">Trip-specific chat unlocked</span>
                </div>
                <div className="flex items-center gap-3 p-3 bg-emerald-50" style={{ borderRadius: '0.75rem' }}>
                  <Check className="text-emerald-600" size={20} />
                  <span className="text-gray-700">Contact exchange enabled</span>
                </div>
                <div className="flex items-center gap-3 p-3 bg-emerald-50" style={{ borderRadius: '0.75rem' }}>
                  <Check className="text-emerald-600" size={20} />
                  <span className="text-gray-700">Priority matching active</span>
                </div>
              </div>
            )}
            
            {paymentResult?.order_id && (
              <p className="text-sm text-gray-500 text-center mb-4">
                Order ID: {paymentResult.order_id}
              </p>
            )}
            
            <div className="space-y-3">
              <button
                onClick={() => navigate(isSuccess ? '/community?tab=matches' : '/payment')}
                className="w-full py-3 bg-gradient-to-r from-teal-500 to-cyan-500 text-white font-bold shadow-lg hover:shadow-xl transition-all"
                style={{ borderRadius: '9999px' }}
              >
                {isSuccess ? 'Go to My Matches' : 'Try Again'}
              </button>
              <button
                onClick={() => navigate('/')}
                className="w-full py-3 bg-gray-100 text-gray-700 font-bold hover:bg-gray-200 transition-all"
                style={{ borderRadius: '9999px' }}
              >
                Back to Home
              </button>
            </div>
          </div>
        </motion.div>
      </div>
    );
  }
  
  return (
    <div className="min-h-screen bg-gradient-to-br from-sky-50 via-white to-teal-50">
      {/* Header */}
      <header className="sticky top-0 z-40 bg-white/80 backdrop-blur-xl border-b border-gray-100">
        <div className="max-w-4xl mx-auto px-4 md:px-6 py-4 flex items-center justify-between">
          <button 
            onClick={() => navigate(-1)}
            className="flex items-center gap-2 text-gray-500 hover:text-teal-600 transition-colors"
          >
            <ArrowLeft size={20} />
          </button>
          
          <div className="flex items-center gap-2">
            <PanchiBirdLogo className="w-9 h-9" />
            <span className="font-bold text-xl text-gray-900">Upgrade to Pro</span>
          </div>
          
          <div className="w-8" /> {/* Spacer */}
        </div>
      </header>
      
      <main className="max-w-4xl mx-auto px-4 md:px-6 py-8">
        {/* Hero Section */}
        <motion.div
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          className="text-center mb-8"
        >
          <div className="w-20 h-20 mx-auto mb-4 bg-gradient-to-br from-amber-400 via-orange-400 to-rose-400 flex items-center justify-center shadow-lg shadow-orange-200"
            style={{ borderRadius: '1.5rem' }}
          >
            <Crown size={40} className="text-white" />
          </div>
          <h1 className="text-3xl md:text-4xl font-black text-gray-900 mb-2">
            Panchi Pro Membership
          </h1>
          <p className="text-gray-600 max-w-md mx-auto">
            Unlock communication features to plan and coordinate trips with matched travelers
          </p>
        </motion.div>
        
        {/* Plan Selection */}
        <motion.div
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ delay: 0.1 }}
          className="grid md:grid-cols-2 gap-4 mb-8"
        >
          {/* Monthly Plan */}
          <button
            onClick={() => setSelectedPlan('monthly')}
            className={`p-6 text-left border-2 transition-all ${
              selectedPlan === 'monthly'
                ? 'border-amber-400 bg-amber-50 shadow-lg'
                : 'border-gray-200 bg-white hover:border-gray-300'
            }`}
            style={{ borderRadius: '1.25rem' }}
          >
            <div className="flex items-start justify-between mb-4">
              <div>
                <h3 className="text-lg font-bold text-gray-900">Monthly</h3>
                <p className="text-sm text-gray-500">Billed monthly</p>
              </div>
              <div className={`w-6 h-6 flex items-center justify-center ${
                selectedPlan === 'monthly' ? 'bg-amber-500' : 'bg-gray-200'
              }`} style={{ borderRadius: '50%' }}>
                {selectedPlan === 'monthly' && <Check size={14} className="text-white" />}
              </div>
            </div>
            <div className="flex items-baseline gap-1">
              <span className="text-3xl font-black text-gray-900">₹299</span>
              <span className="text-gray-500">/month</span>
            </div>
          </button>
          
          {/* Annual Plan */}
          <button
            onClick={() => setSelectedPlan('annual')}
            className={`p-6 text-left border-2 transition-all relative ${
              selectedPlan === 'annual'
                ? 'border-amber-400 bg-amber-50 shadow-lg'
                : 'border-gray-200 bg-white hover:border-gray-300'
            }`}
            style={{ borderRadius: '1.25rem' }}
          >
            <span className="absolute -top-3 right-4 px-3 py-1 bg-emerald-500 text-white text-xs font-bold" style={{ borderRadius: '9999px' }}>
              Save 44%
            </span>
            <div className="flex items-start justify-between mb-4">
              <div>
                <h3 className="text-lg font-bold text-gray-900">Annual</h3>
                <p className="text-sm text-gray-500">Billed yearly</p>
              </div>
              <div className={`w-6 h-6 flex items-center justify-center ${
                selectedPlan === 'annual' ? 'bg-amber-500' : 'bg-gray-200'
              }`} style={{ borderRadius: '50%' }}>
                {selectedPlan === 'annual' && <Check size={14} className="text-white" />}
              </div>
            </div>
            <div className="flex items-baseline gap-1">
              <span className="text-3xl font-black text-gray-900">₹1,999</span>
              <span className="text-gray-500">/year</span>
            </div>
            <p className="text-sm text-emerald-600 font-medium mt-2">
              That's just ₹167/month
            </p>
          </button>
        </motion.div>
        
        {/* Features */}
        <motion.div
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ delay: 0.2 }}
          className="bg-white p-6 shadow-lg mb-8"
          style={{ borderRadius: '1.25rem' }}
        >
          <h2 className="text-lg font-bold text-gray-900 mb-4">What you get with Pro</h2>
          <div className="space-y-4">
            {PRO_FEATURES.map((feature, index) => (
              <div key={index} className="flex items-center gap-4">
                <div className="w-10 h-10 bg-amber-100 flex items-center justify-center flex-shrink-0" style={{ borderRadius: '0.75rem' }}>
                  <feature.icon size={20} className="text-amber-600" />
                </div>
                <span className="text-gray-700">{feature.text}</span>
              </div>
            ))}
          </div>
        </motion.div>
        
        {/* Payment Methods Info */}
        <motion.div
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ delay: 0.3 }}
          className="bg-gray-50 p-4 mb-8"
          style={{ borderRadius: '1rem' }}
        >
          <p className="text-sm text-gray-600 text-center mb-3">Secure payment powered by Razorpay</p>
          <div className="flex items-center justify-center gap-4">
            <div className="flex items-center gap-2 text-gray-500">
              <CreditCard size={20} />
              <span className="text-sm">Cards</span>
            </div>
            <div className="flex items-center gap-2 text-gray-500">
              <Smartphone size={20} />
              <span className="text-sm">UPI</span>
            </div>
            <div className="flex items-center gap-2 text-gray-500">
              <Building2 size={20} />
              <span className="text-sm">Net Banking</span>
            </div>
          </div>
        </motion.div>
        
        {/* CTA Button */}
        <motion.div
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ delay: 0.4 }}
        >
          <button
            onClick={handlePayment}
            disabled={loading}
            className="w-full py-4 bg-gradient-to-r from-amber-500 via-orange-500 to-rose-500 text-white text-lg font-bold shadow-lg shadow-orange-200 hover:shadow-orange-300 transition-all flex items-center justify-center gap-2 disabled:opacity-70"
            style={{ borderRadius: '9999px' }}
            data-testid="pay-now-btn"
          >
            {loading ? (
              <>
                <Loader2 size={20} className="animate-spin" />
                Processing...
              </>
            ) : (
              <>
                <Crown size={20} />
                Pay ₹{selectedPlan === 'monthly' ? '299' : '1,999'} & Upgrade
              </>
            )}
          </button>
          
          <p className="text-center text-sm text-gray-500 mt-4">
            Cancel anytime. No questions asked.
          </p>
        </motion.div>
        
        {/* Trust Badges */}
        <motion.div
          initial={{ opacity: 0 }}
          animate={{ opacity: 1 }}
          transition={{ delay: 0.5 }}
          className="mt-8 flex items-center justify-center gap-6 text-gray-400"
        >
          <div className="flex items-center gap-2">
            <Shield size={16} />
            <span className="text-sm">Secure Payment</span>
          </div>
          <div className="flex items-center gap-2">
            <Check size={16} />
            <span className="text-sm">Instant Activation</span>
          </div>
        </motion.div>
      </main>
    </div>
  );
}
